import React, {useEffect, useState} from "react";
import DateRangePicker from "react-date-range/dist/components/DateRangePicker";
import {parseISO, format, addDays, subDays, isSameDay, startOfDay, endOfDay, startOfMonth, endOfMonth, addMonths, subMonths, startOfYear, endOfYear, subYears, formatISO} from "date-fns";
import {DateFormat} from "../../Helpers/DateTimeHelper";
import ActionButton from "../Buttons/ActionButton";
import {MANAGEMENT_TYPE} from "../../Modules/Visitors/Visitors";

const DateRangeButton = (props) => {
    // The date range dates are stored as date objects but are
    // sent back as iso date strings
    let [dateRange, setDateRange] = useState([false, false]);
    let [isOpen, setIsOpen] = useState(false)

    useEffect(() => {
        resetDateRange();
    }, [props.startDate, props.endDate]);

    const resetDateRange = () => {
        if (!props.startDate || !props.endDate) {
            setDateRange([new Date(), new Date()]);
        } else {
            setDateRange([
                parseISO(props.startDate),
                parseISO(props.endDate),
            ])
        }
    }

    const getButtonIcon = () => {
        if (props.isSmall) {
            return 'fa-calendar';
        }
    }

    const getButtonText = () => {
        if (props.isSmall) {
            return '';
        }

        if (!props.startDate || !props.endDate) {
            return 'Date Range';
        }

        let start = parseISO(props.startDate);
        let end = parseISO(props.endDate);

        return `${format(start, DateFormat.HUMAN_SLASHED_DATE)} - ${format(end, DateFormat.HUMAN_SLASHED_DATE)}`
    }

    const getBadgeContent = () => {
        if (props.hideBadge) {
            return null;
        }
        if (!props.startDate || !props.endDate) {
            return null;
        }

        return '';
    }

    const getDateRangeForPickerComponent = () => {
        return [{
            startDate: dateRange[0],
            endDate: dateRange[1],
            key: 'selection',
        }]
    }

    /**
     * React-date-range is a bit fussy with how you have to specify custom static ranges
     * @param label
     * @param startDate
     * @param endDate
     * @returns {{hasCustomRendering: boolean, isSelected: (function(*)), range: (function(): {endDate: *, startDate: *}), label}}
     */
    const makeStaticRange = (label, startDate, endDate) => {
        return {
            label: label,
            hasCustomRendering: false,
            range: () => ({
                startDate: startDate,
                endDate: endDate,
            }),
            isSelected: function isSelected(range) {
                var definedRange = this.range();
                return (0, isSameDay)(range.startDate, definedRange.startDate) && (0, isSameDay)(range.endDate, definedRange.endDate);
            },
        };
    }

    const getStaticRanges = () => {
        if (props.type === MANAGEMENT_TYPE.UPCOMING) {
            return [
                makeStaticRange('Today', new Date(), new Date()),
                makeStaticRange('Tomorrow', addDays(new Date(), 1), addDays(new Date(), 1)),
                makeStaticRange('Next 7 Days', new Date(), addDays(new Date(), 7)),
                makeStaticRange('Next Month', startOfMonth(addMonths(new Date(), 1)), endOfMonth(addMonths(new Date(), 1)) ),
            ]
        } else {
            return [
                makeStaticRange('Last 7 Days', subDays(new Date(), 7), new Date()),
                makeStaticRange('Last Month', startOfMonth(subMonths(new Date(), 1)), endOfMonth(subMonths(new Date(), 1))),
                makeStaticRange('This Month', startOfMonth(new Date()), endOfMonth(new Date())),
                makeStaticRange('Last 90 Days', subDays(new Date(), 90), new Date()),
                makeStaticRange('Last Year', startOfYear(subYears(new Date(), 1)), endOfYear(subYears(new Date(), 1))),
                makeStaticRange('This Year', startOfYear(new Date()), endOfYear(new Date())),
            ].filter(range => !props.minDate || range.range().startDate > props.minDate)
        }
    }
    const handleChange = (item) => {
        if (item.selection) {
            setDateRange([
                startOfDay(item.selection.startDate),
                endOfDay(item.selection.endDate)
            ])
        } else if (item.range1) {
            setDateRange([
                startOfDay(item.range1.startDate),
                endOfDay(item.range1.endDate)
            ])
        }
    }

    const handleCancel = (event) => {
        resetDateRange();
        setIsOpen(false);
    }

    const handleClear = (event) => {
        props.onChange(false, false)
        setIsOpen(false);
    }

    const handleApply = (event) => {
        // Convert date objects back to serializable strings to store in state
        props.onChange(
            formatISO(dateRange[0]),
            formatISO(dateRange[1])
        )
        setIsOpen(false);
    }

    return (<>
        <ActionButton
            onClick={(evt) => setIsOpen(!isOpen)}
            title={getButtonText()}
            icon={getButtonIcon()}
            isActive={isOpen}
            badgeContent={getBadgeContent()}
            className={props.className}
        />

        {isOpen && (<>
            <div
                className="dateRangeShield"
                onClick={(e) => { setIsOpen(false)}}
            ></div>
            <div className={`date-range-picker-outer ${props.isCentered ? 'is-centered' : ''}`}>
                <DateRangePicker
                    onChange={handleChange}
                    showSelectionPreview={true}
                    moveRangeOnFirstSelection={false}
                    months={2}
                    staticRanges={getStaticRanges()}
                    inputRanges={[]}
                    ranges={getDateRangeForPickerComponent()}
                    direction="horizontal"
                    minDate={props.minDate}
                    maxDate={props.maxDate}
                />

                <div className="grid-x date-range-footer">
                    { (props.message) && (
                        <div className="cell small-12 text-center padding-bottom-1">
                            <div style={{fontSize: 15, color: 'darkgrey'}}>
                                {props.message}
                            </div>
                        </div>
                    )}
                    <div className="cell small-4">
                        <div className="button-group">
                            <button
                                className="button secondary"
                                onClick={handleClear}
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                    <div className="cell small-8">
                        <div className="button-group align-right">
                            <button
                                className="button clear"
                                onClick={handleCancel}
                            >
                                Cancel
                            </button>
                            <button
                                className="button primary"
                                onClick={handleApply}
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>)}
    </>);
}

export default DateRangeButton
